import {
  DeliveryDining,
  LocalShippingOutlined,
  MyLocationOutlined,
  RefreshOutlined,
  RouteOutlined,
} from "@mui/icons-material";
import Button from "components/common/Button";
import GoogleAddress from "components/common/GoogleAddress";
import api from "config/api";
import { useAppSelector } from "hooks/store";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { convertNumberToHex } from "screens";
import { fetchDeliveryChargesForAddress } from "utils/deliveryCharges";

const getFirstValue = (item: any, keys: string[]) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "") {
      return item[key];
    }
  }
  return undefined;
};

const unwrapList = (response: any) => {
  const possibleLists = [
    response,
    response?.data,
    response?.Data,
    response?.result,
    response?.Result,
    response?.response,
    response?.Response,
    response?.items,
    response?.Items,
    response?.deliveryCharges,
    response?.DeliveryCharges,
  ];

  return possibleLists.find(Array.isArray) || [];
};

const formatNumber = (value: any, fallback = "--") => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : fallback;
};

const normalizeChargeRow = (item: any, index: number) => {
  const from = getFirstValue(item, [
    "distanceFrom",
    "DistanceFrom",
    "fromDistance",
    "FromDistance",
    "fromKm",
    "FromKm",
    "kmFrom",
    "KmFrom",
    "minDistance",
    "MinDistance",
    "minKm",
    "MinKm",
    "delvKmFrom",
    "DelvKmFrom",
  ]);

  const to = getFirstValue(item, [
    "delvKm",
    "DelvKm",
    "distanceTo",
    "DistanceTo",
    "toDistance",
    "ToDistance",
    "toKm",
    "ToKm",
    "kmTo",
    "KmTo",
    "maxDistance",
    "MaxDistance",
    "maxKm",
    "MaxKm",
    "distance",
    "Distance",
  ]);

  const charge = getFirstValue(item, [
    "delvCharges",
    "DelvCharges",
    "charges",
    "Charges",
    "charge",
    "Charge",
    "amount",
    "Amount",
    "price",
    "Price",
    "rate",
    "Rate",
    "deliveryCharge",
    "DeliveryCharge",
  ]);

  const id =
    getFirstValue(item, [
      "id",
      "Id",
      "deliveryChargeId",
      "DeliveryChargeId",
      "delvId",
      "DelvId",
      "delvChargeId",
      "DelvChargeId",
      "dcId",
      "DcId",
    ]) ?? index + 1;

  return {
    id,
    from,
    to,
    charge,
    raw: item,
  };
};

const DeliveryDistanceCharges = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkAddress, setCheckAddress] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<{
    charges: number;
    distance: number | null;
  } | null>(null);
  const { settings } = useAppSelector(({ main }) => main);
  const themeColor = convertNumberToHex(settings.themeColor);
  const isDistanceChargeEnabled = Number(settings?.ApplyDeliveryChargesAsKm || 0) === 1;

  const normalizedRows = useMemo(
    () =>
      rows
        .map((item, index) => normalizeChargeRow(item, index))
        .sort((a, b) => {
          const aDistance = Number(a.to);
          const bDistance = Number(b.to);

          if (Number.isFinite(aDistance) && Number.isFinite(bDistance)) {
            return aDistance - bDistance;
          }

          if (Number.isFinite(aDistance)) return -1;
          if (Number.isFinite(bDistance)) return 1;
          return Number(a.id) - Number(b.id);
        }),
    [rows]
  );

  const fetchRows = () => {
    setLoading(true);
    api
      .get("/api/DeliveryCharge/GetDeliveryCharges")
      .then(({ data }) => {
        setRows(unwrapList(data));
      })
      .catch((error) => {
        toast.error(
          typeof error === "string"
            ? error
            : "Unable to load delivery distance charges"
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const checkDeliveryCharge = () => {
    const address = checkAddress.trim();
    if (!address) {
      toast.error("Please enter a delivery address");
      return;
    }

    setChecking(true);
    setCheckResult(null);
    fetchDeliveryChargesForAddress(address)
      .then((result) => setCheckResult(result))
      .catch(() => toast.error("Unable to check delivery charge for this address"))
      .finally(() => setChecking(false));
  };

  return (
    <div className="w-full p-4">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-lg text-white"
              style={{ backgroundColor: themeColor }}
            >
              <RouteOutlined className="!text-[22px]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delivery Distance Charges
              </h3>
              <p className="text-sm text-gray-500">
                Check distance based delivery pricing and charge rules
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold ring-1 ${
                isDistanceChargeEnabled
                  ? "bg-green-50 text-green-700 ring-green-200"
                  : "bg-red-50 text-red-700 ring-red-200"
              }`}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  isDistanceChargeEnabled ? "bg-green-500" : "bg-red-500"
                }`}
              />
              KM Delivery Charges{" "}
              {isDistanceChargeEnabled ? "Enabled" : "Disabled"}
            </div>

            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              onClick={fetchRows}
              disabled={loading}
            >
              <RefreshOutlined className={`!text-[19px] ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid gap-4 border-b border-gray-100 p-5 xl:grid-cols-[1fr_320px]">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-900">
              <MyLocationOutlined className="!text-[19px] text-gray-500" />
              Price check by address
            </div>
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="min-w-0 flex-1">
                <GoogleAddress
                  value={checkAddress}
                  onChange={setCheckAddress}
                  className="[&_.MuiInputBase-root]:bg-white"
                />
              </div>
              <Button
                className="!h-10 !rounded-lg !px-5"
                disabled={checking}
                onClick={checkDeliveryCharge}
              >
                {checking ? "Checking" : "Check Price"}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <DeliveryDining className="!text-[19px] text-gray-500" />
              Result
            </div>
            {checkResult ? (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-xs font-semibold text-gray-500">Distance</p>
                  <p className="text-lg font-bold text-gray-900">
                    {checkResult.distance === null
                      ? "--"
                      : `${formatNumber(checkResult.distance)} km`}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-xs font-semibold text-gray-500">Charge</p>
                  <p className="text-lg font-bold text-gray-900">
                    CA${formatNumber(checkResult.charges)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500">
                Enter an address to preview delivery distance and charge.
              </p>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-5 py-3">Rule</th>
                <th className="px-4 py-3">From Distance</th>
                <th className="px-4 py-3">To Distance</th>
                <th className="px-4 py-3">Delivery Charge</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {normalizedRows.map((item, index) => (
                <tr key={`${item.id}-${index}`} className="transition hover:bg-gray-50">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 font-semibold text-gray-900">
                      <LocalShippingOutlined className="!text-[18px] text-gray-400" />
                      Distance Rule {index + 1}
                    </div>
                    <div className="text-xs text-gray-500">ID: {String(item.id)}</div>
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-800">
                    {item.from === undefined ? "--" : `${formatNumber(item.from)} km`}
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-800">
                    {formatNumber(item.to)} km
                  </td>
                  <td className="px-4 py-4 text-sm font-bold text-gray-900">
                    CA${formatNumber(item.charge, "0.00")}
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 ring-1 ring-green-200">
                      Active
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading && !normalizedRows.length && (
            <div className="bg-white p-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="mb-2 h-12 animate-pulse rounded-lg bg-gray-100"
                />
              ))}
            </div>
          )}

          {!loading && !normalizedRows.length && (
            <div className="flex flex-col items-center justify-center gap-2 px-5 py-12 text-center">
              <RouteOutlined className="!text-[34px] text-gray-300" />
              <p className="text-sm font-semibold text-gray-700">
                No delivery distance charges found
              </p>
              <p className="text-sm text-gray-500">
                The API did not return any distance price rules.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryDistanceCharges;
